import { PaginatedSelector } from '@chewy/chat-library-components';
import OrderCardReturn from './OrderCardReturn';
import type { ChatMessage, ReturnCardData, RequestScrollCallback } from '@chewy/chat-library-common';

const MAX_RETURNS_PER_PAGE = 3;

interface OrderCardReturnSelectorProps {
  /**
   * Array of return data to display
   */
  data: ReturnCardData[];
  /**
   * Layout direction for the cards
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Chat message associated with the orders
   */
  message?: ChatMessage;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Page offset for the cards
   * @default 3
   */
  pageOffset?: number;
  /**
   * Callback function to request scrolling to a selected item
   */
  requestScroll?: RequestScrollCallback;
}

/**
 * Displays a list of selectable OrderCardReturn components with pagination
 * Uses PaginatedSelector to handle pagination and action buttons
 */
export default function OrderCardReturnSelector({
  data,
  direction = 'vertical',
  message,
  className,
  pageOffset = MAX_RETURNS_PER_PAGE,
  requestScroll,
}: OrderCardReturnSelectorProps) {
  return (
    <PaginatedSelector
      data={data}
      renderItem={(pkg, index, { ref, handleSelect }) => {
        return (
          <OrderCardReturn
            key={pkg.orderId ?? index}
            ref={ref}
            data={[{ ...pkg, returnNumber: `${index + 1}`, totalReturns: data.length }]}
            onSelect={() => handleSelect(message?.responseTimestamp)}
          />
        );
      }}
      direction={direction}
      className={className}
      pageSize={pageOffset}
      itemType="return"
      requestScroll={requestScroll}
      showMoreAction={[{
        defaultAction: {
          type: 'button',
          text: 'Show more',
          payload: {
            action: 'SHOW_MORE_RETURNS',
          },
        },
      }]}
      secondaryAction={[{
        defaultAction: {
          type: 'button',
          text: 'Need help with a different return',
          payload: {
            action: 'SHOW_DIFFERENT_RETURN_OPTIONS',
          },
        },
      }]}
    />
  );
}
